// external imports
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Autocomplete,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import { CloudUploadOutlined, Close, ArrowBack } from '@mui/icons-material';
import { toast } from 'react-toastify';

// internal imports
import Layout from 'Component/Layouts/layout';
import useApiCaller from 'hooks/apiCaller';
import { FileTypeBadge, formatBytes, parseTaskError } from './common';

const ACCEPTED = '.xlsx,.xls,.pdf,.jpg,.jpeg,.png';

const CreateTask = () => {
  const apiCaller = useApiCaller();
  const navigate = useNavigate();

  // form fields
  const [taskName, setTaskName] = React.useState('');
  const [taskDescription, setTaskDescription] = React.useState('');
  const [taskType] = React.useState('translation'); // only translation for now
  const [files, setFiles] = React.useState([]);
  const [selectedLangs, setSelectedLangs] = React.useState([]); // [{ code, name, translators }]

  // active language configs (options for the multiselect) — only ACTIVE ones,
  // because create() rejects any language without an active translator.
  const [langOptions, setLangOptions] = React.useState([]);
  const [langsLoading, setLangsLoading] = React.useState(true);

  const [submitting, setSubmitting] = React.useState(false);
  const [formError, setFormError] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef = React.useRef(null);

  // load active configs for the language picker
  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLangsLoading(true);
        const response = await apiCaller.get('/task/langconfig');
        const configs = response?.data?.data?.configs ?? [];
        const options = configs
          .filter((c) => c.isActive)
          .map((c) => ({
            code: c.languageCode,
            name: c.languageName,
            translators: c.translators ?? [],
          }));
        if (active) {
          setLangOptions(options);
          setSelectedLangs(options); // prefill with every available language
        }
      } catch (err) {
        if (active) toast.error(parseTaskError(err, 'Failed to load languages.').message);
      } finally {
        if (active) setLangsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // number of distinct translators across the selected languages (for the banner)
  const translatorCount = React.useMemo(() => {
    const ids = new Set();
    selectedLangs.forEach((lang) =>
      (lang.translators || []).forEach((t) => {
        if (t.employeeId != null) ids.add(t.employeeId);
      })
    );
    return ids.size;
  }, [selectedLangs]);

  // ---- file handling ----
  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;
    setFiles((prev) => {
      // de-dupe by name + size so re-dropping the same file doesn't stack
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      const next = [...prev];
      incoming.forEach((f) => {
        const key = `${f.name}:${f.size}`;
        if (!seen.has(key)) {
          seen.add(key);
          next.push(f);
        }
      });
      return next;
    });
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  // ---- submit ----
  const validate = () => {
    if (!taskName.trim()) return 'Please enter a task name.';
    if (selectedLangs.length === 0) return 'Please select at least one target language.';
    if (files.length === 0) return 'At least one source file is required.';
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setFormError(validationError);
      return;
    }
    try {
      setSubmitting(true);
      setFormError(null);

      const formData = new FormData();
      formData.append('taskName', taskName.trim());
      formData.append('taskDescription', taskDescription.trim());
      formData.append('taskType', taskType);
      formData.append('targetLanguages', JSON.stringify(selectedLangs.map((l) => l.code)));
      files.forEach((file) => formData.append('files', file));

      const response = await apiCaller.post('/task/create', formData);
      const createdTask = response?.data?.data?.task;
      toast.success('Task created and translators assigned.');
      if (createdTask?._id) {
        navigate(`/tasks/${createdTask._id}`);
      } else {
        navigate('/tasks');
      }
    } catch (err) {
      // 400s here name the problem, e.g. "No active translator configured for: Tamil"
      setFormError(parseTaskError(err, 'Failed to create task. Please try again.').message);
    } finally {
      setSubmitting(false);
    }
  };

  const labelSx = { fontSize: 13, fontWeight: 600, color: '#334155', mb: 0.75 };

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 2, md: 3 },
        }}
      >
        {/* back link */}
        <Button
          onClick={() => navigate('/tasks')}
          startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
          sx={{
            textTransform: 'none',
            color: '#64748b',
            mb: 1.5,
            '&:hover': { background: 'transparent', color: '#334155' },
          }}
        >
          Back to tasks
        </Button>

        <Box
          sx={{
            maxWidth: 760,
            mx: 'auto',
            background: '#ffffff',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            p: { xs: 2.5, md: 4 },
          }}
        >
          <Typography variant="h5" fontWeight="700" sx={{ color: '#1e293b', mb: 0.5 }}>
            Create Translation Task
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
            Upload the source documents and pick the target languages — each is auto-assigned to its
            configured translator.
          </Typography>

          {/* Task name */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={labelSx}>Task Name</Typography>
            <TextField
              fullWidth
              size="small"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              placeholder="e.g. Employee Handbook 2026 – Regional Translation"
              InputProps={{ sx: { borderRadius: '10px' } }}
            />
          </Box>

          {/* Description */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={labelSx}>Task Description</Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              placeholder="What needs translating and any context for the translators…"
              InputProps={{ sx: { borderRadius: '10px' } }}
            />
          </Box>

          {/* Task type */}
          <Box sx={{ mb: 2.5, maxWidth: 260 }}>
            <Typography sx={labelSx}>Task Type</Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={taskType}
              disabled
              InputProps={{ sx: { borderRadius: '10px', background: '#f8fafc' } }}
            >
              <MenuItem value="translation">Translation</MenuItem>
            </TextField>
          </Box>

          {/* Source files */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={labelSx}>Source File(s)</Typography>
            <Box
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              sx={{
                border: `1.5px dashed ${dragOver ? '#6366f1' : '#cbd5e1'}`,
                borderRadius: '12px',
                background: dragOver ? '#eef2ff' : '#f8fafc',
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: '#6366f1', background: '#eef2ff' },
              }}
            >
              <CloudUploadOutlined sx={{ color: '#94a3b8', fontSize: 28, mb: 0.5 }} />
              <Typography sx={{ fontSize: 14, color: '#64748b' }}>
                Drop file here or <span style={{ color: '#4f46e5', fontWeight: 600 }}>browse</span>{' '}
                — .xlsx, .pdf, .jpg, .png
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED}
                hidden
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = ''; // allow re-selecting the same file
                }}
              />
            </Box>

            {/* selected file chips */}
            {files.length > 0 && (
              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {files.map((file, idx) => (
                  <Box
                    key={`${file.name}:${file.size}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      p: 1,
                      pr: 1.5,
                      background: '#fff',
                    }}
                  >
                    <FileTypeBadge fileType="" fileName={file.name} size={40} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: '#1e293b',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {file.name}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                        {formatBytes(file.size)}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => removeFile(idx)}>
                      <Close sx={{ fontSize: 18, color: '#94a3b8' }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Target languages */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={labelSx}>Target Languages</Typography>
            <Autocomplete
              multiple
              options={langOptions}
              loading={langsLoading}
              value={selectedLangs}
              onChange={(_, value) => setSelectedLangs(value)}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    key={option.code}
                    sx={{
                      background: '#eef2ff',
                      color: '#4338ca',
                      fontWeight: 600,
                      borderRadius: '999px',
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={selectedLangs.length ? '' : 'Select languages…'}
                  InputProps={{
                    ...params.InputProps,
                    sx: { borderRadius: '10px' },
                    endAdornment: (
                      <>
                        {langsLoading ? (
                          <CircularProgress size={16} sx={{ color: '#6366f1' }} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {!langsLoading && langOptions.length === 0 && (
              <Typography sx={{ fontSize: 12.5, color: '#b45309', mt: 0.75 }}>
                No active language configs found. Add one in Language Config first.
              </Typography>
            )}
          </Box>

          {/* assignment summary banner */}
          {selectedLangs.length > 0 && (
            <Box
              sx={{
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '10px',
                px: 2,
                py: 1.5,
                mb: 2.5,
              }}
            >
              <Typography sx={{ fontSize: 14, color: '#1d4ed8' }}>
                This task will be assigned to <b>{translatorCount}</b> translator
                {translatorCount === 1 ? '' : 's'} across <b>{selectedLangs.length}</b> language
                {selectedLangs.length === 1 ? '' : 's'}.
              </Typography>
            </Box>
          )}

          {/* error */}
          {formError && (
            <Alert severity="error" sx={{ borderRadius: '10px', mb: 2.5 }}>
              {formError}
            </Alert>
          )}

          {/* actions */}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={submitting}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '10px',
                px: 3,
                py: 1,
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)' },
              }}
            >
              {submitting ? 'Creating…' : 'Create Task'}
            </Button>
            <Button
              onClick={() => navigate('/tasks')}
              variant="outlined"
              disabled={submitting}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '10px',
                px: 3,
                borderColor: '#e2e8f0',
                color: '#475569',
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default CreateTask;
